import type { FC } from 'react';
import type { GameDto } from '@/queries/games/games.schema';
import { useTranslations } from 'next-intl';
import { isEmpty } from '@/utils/objects.utils';
import { detailsNameMap, GameTabsMap, hasDetailsValue, hiddenDetailKeys } from '../utils/detailed-game.utils';

type GameDetailsInfo = NonNullable<GameDto['details']>;

const detailsDisplayOrder = [
  'category',
  'subCategory',
  'rtpType',
  'rtpRange',
  'rtp',
  'topPrize',
  'topPrizeRange',
  'volatility',
  'minBet',
  'minBetRange',
  'maxBet',
  'maxBetRange',
  'gameMechanics',
  'subGameMechanics',
  'subSubGameMechanics',
  'grid',
  'features',
  'subFeature',
  'promoTools',
  'jackpotType',
  'subJackpotType',
  'themes',
  'subThemes',
] as const;

interface GameDetailsProps {
  details?: GameDetailsInfo | null;
}

export const GameDetails: FC<GameDetailsProps> = ({ details }) => {
  const t = useTranslations('game');

  const renderValue = (key: string, value: GameDetailsInfo[keyof GameDetailsInfo]) => {
    if (Array.isArray(value)) {
      return value.join(', ');
    }

    if (key === 'rtp' && typeof value === 'number') {
      return `${value}%`;
    }

    return value;
  };

  return (
    <section id={GameTabsMap.details.id} className="max-w-218 3xl:max-w-full">
      <h3 className="mb-4 text-xl leading-[1.3] font-medium text-light">{t('game-details.game-details')}</h3>
      {
        !isEmpty(details) && (
          (() => {
            const entries = Object.entries(details ?? {})
              .filter(([key]) => !hiddenDetailKeys.has(key as keyof GameDetailsInfo))
              .filter(([, value]) => hasDetailsValue(value))
              .sort(([firstKey], [secondKey]) => {
                const firstIndex = detailsDisplayOrder.indexOf(firstKey as typeof detailsDisplayOrder[number]);
                const secondIndex = detailsDisplayOrder.indexOf(secondKey as typeof detailsDisplayOrder[number]);

                if (firstIndex === -1 && secondIndex === -1) {
                  return 0;
                }
                if (firstIndex === -1) {
                  return 1;
                }
                if (secondIndex === -1) {
                  return -1;
                }

                return firstIndex - secondIndex;
              });
            const midPoint = Math.ceil(entries.length / 2);
            const firstColumn = entries.slice(0, midPoint);
            const secondColumn = entries.slice(midPoint);

            return (
              <div className="grid md:grid-cols-2 grid-cols-1 md:gap-x-7.5">
                <dl className="text-c300">
                  {firstColumn.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[6.75rem_1fr] border-b gap-6 border-violet-dark pt-1.5 pb-1.25">
                      <dt className="paragraph-m-regular">{ detailsNameMap[k] || k}</dt>
                      <dd className="font-medium">{ renderValue(k, v) }</dd>
                    </div>
                  ))}
                </dl>
                <dl className="text-c300">
                  {secondColumn.map(([k, v]) => (
                    <div key={k} className="grid grid-cols-[6.75rem_1fr] border-b gap-6 border-violet-dark pt-1.5 pb-1.25">
                      <dt className="paragraph-m-regular">{ detailsNameMap[k] || k}</dt>
                      <dd className="font-medium">{ renderValue(k, v) }</dd>
                    </div>
                  ))}
                </dl>
              </div>
            );
          })()
        )
      }
    </section>
  );
};
